import React from 'react';
import { Link } from 'react-router-dom';
import { LuZap, LuChevronRight } from 'react-icons/lu';
import Card from '../../ui/Card/Card';
import Badge from '../../ui/Badge/Badge';
import CurrencyAmount from '../../ui/CurrencyAmount/CurrencyAmount';
import { sessionStatusCopy } from '../../../utils/statusCopy';
import { resolveBillingDisplay } from '../../../utils/billingDisplay';
import { formatSessionDate, formatSessionTime } from '../../../utils/formatDate';
import { stateCopy } from '../../../chargerUx/copy';
import './ChargingSessionCard.less';

function ChargingSessionCard({ session, to }) {
  const status = sessionStatusCopy(session.paymentStatus === 'paid' ? 'completed'
    : session.endedAt ? 'completed'
    : 'pending');
  // Charger UX: when the API provides the canonical outcome the badge shows
  // it, so this card and the Activity row never disagree about a session.
  // Without it the card renders exactly as before.
  const badge = (session.canonical ? stateCopy(session.canonical.sessionState) : null) || status;
  const cost = session.capturedCents ?? null;
  const currency = session.currency || 'BBD';
  const startTs = session.startedAt || session.endedAt;
  // Amount, session type and billing status are the backend's
  // `billing.display`; without it, the recorded amount only.
  const billing = resolveBillingDisplay(session, {
    amount: <CurrencyAmount cents={cost} currency={currency} />,
    paymentStatus: session.paymentStatus,
  });

  return (
    <Card as={to ? Link : 'div'} to={to} interactive={!!to} className="gc-session-card">
      {/* Grid children are FLAT — icon | body | chevron — so the
          chevron stays vertically centred on the far right at every
          viewport width. See ChargingSessionCard.less for the
          `auto minmax(0,1fr) auto` template that keeps this row
          from wrapping the arrow to a new line. */}
      <span className="gc-session-card-icon" aria-hidden="true"><LuZap /></span>
      <div className="gc-session-card-text">
        <div className="gc-session-card-row">
          <span className="gc-session-card-station">
            {session.stationName || 'Charging session'}
          </span>
          <Badge tone={badge.tone}>{badge.label}</Badge>
        </div>
        <p className="gc-session-card-meta">
          <span>{formatSessionDate(startTs)}</span>
          {formatSessionTime(startTs) && <span> · {formatSessionTime(startTs)}</span>}
          {session.plateRaw && <span> · {session.plateRaw}</span>}
        </p>
        <div className="gc-session-card-stats">
          <span>
            {typeof session.energyKwh === 'number'
              ? `${session.energyKwh.toFixed(2)} kWh`
              : '—'}
          </span>
          <span aria-hidden="true"> · </span>
          <span>
            {billing.amount.label}{' '}
            <span className="gc-session-card-cost">{billing.amount.value}</span>
          </span>
        </div>
        {billing.fromServer && (
          <p className="gc-session-card-billing">
            {billing.sessionTypeLabel && <span>{billing.sessionTypeLabel}</span>}
            {billing.sessionTypeLabel && <span aria-hidden="true"> · </span>}
            <span className="gc-session-card-status">{billing.status.label}</span>
          </p>
        )}
      </div>
      {to && (
        <span className="gc-session-card-chevron-slot" aria-hidden="true">
          <LuChevronRight className="gc-session-card-chevron" />
        </span>
      )}
    </Card>
  );
}

export default ChargingSessionCard;
